// The workspace `cyborg:sync` drain loop (testable outside app.svelte.ts). CYBORG-1203:
// advance to `nextSeq` on EVERY applied page, the last one included; breaking on
// `!hasMore` first left the cursor stuck. seq is an integer read as `seq > cursor`, so
// the last row's seq loses nothing (not the ms-truncated `after` cursor).

export interface SyncPage<M> {
  messages: M[];
  hasMore?: boolean;
  nextSeq?: number;
}

export interface SyncDrainDeps<M> {
  fetchPage: (cursor: number) => Promise<SyncPage<M>>;
  /** Union the page into whatever is on screen. */
  applyPage: (messages: M[]) => void;
  /** Still the workspace the drain started for? Re-checked after every await. */
  isCurrent: () => boolean;
  /** Persist the drain high-water (monotonic on the storage side). */
  saveCursor: (cursor: number) => void;
}

export const SYNC_DRAIN_MAX_PAGES = 50;

/** Returns the cursor the drain ended at. */
export async function drainWorkspaceSync<M>(
  startCursor: number,
  deps: SyncDrainDeps<M>,
  maxPages = SYNC_DRAIN_MAX_PAGES,
): Promise<number> {
  let cursor = startCursor;
  for (let page = 0; page < maxPages; page++) {
    // PER-PAGE catch: a failed page stops the drain; the pages already applied stand
    // (the apply is a union by id) and the caller's burst continues.
    let syncPage: SyncPage<M>;
    try {
      syncPage = await deps.fetchPage(cursor);
    } catch {
      // intentional: a failed page ends the drain; applied pages stand and the burst's panel reconcile continues
      break;
    }
    if (!deps.isCurrent()) break;
    const { messages, hasMore, nextSeq } = syncPage;
    deps.applyPage(messages);
    // Advance on EVERY applied page, the last one included.
    const advanced = typeof nextSeq === "number" && nextSeq > cursor;
    if (advanced) {
      cursor = nextSeq;
      deps.saveCursor(cursor);
    }
    // No progress with hasMore set would loop on the same page: stop.
    if (!hasMore || !advanced) break;
  }
  return cursor;
}
